import { useCallback, useEffect, useMemo, useState, type RefObject } from 'react';
import type { IChartApi, ISeriesApi } from 'lightweight-charts';
import { activeInstructionChipTitle, instructionStatusChartMarker } from '@/lib/instructionDisplay';
import {
  formatInstructionThresholdAxisCompactLabel,
  formatInstructionThresholdLineLabel,
  instructionThresholdToChartPrice,
  notifyInstructionsChanged,
  type InstructionThresholdLine,
} from '@/lib/instructionThresholdLines';
import {
  isManualTradingConfigured,
  pauseInstruction,
  resumeInstruction,
  updateInstruction,
  type InstructionView,
  type ThresholdMode,
} from '@/services/manualTradingApi';
import './InstructionThresholdAxisOverlay.css';

type InstructionEditDraft = {
  targetNotional: string;
  perOrderNotional: string;
  thresholdMode: ThresholdMode;
  threshold: string;
  minIntervalMs: string;
};

type AxisItemLayout = {
  line: InstructionThresholdLine;
  top: number;
};

type Props = {
  chartRef: RefObject<IChartApi | null>;
  chartContainerRef: RefObject<HTMLDivElement | null>;
  openSpreadSeriesRef: RefObject<ISeriesApi<'Line'> | null>;
  closeSpreadSeriesRef: RefObject<ISeriesApi<'Line'> | null>;
  lines: InstructionThresholdLine[];
  instructions: InstructionView[];
  chartSpreadScale: number;
  enabled: boolean;
};

function isPersistentCloseInstruction(row: InstructionView): boolean {
  return row.action === 'Close' && row.validity === 'persistent';
}

function draftFromInstruction(row: InstructionView): InstructionEditDraft {
  const thresholdMode =
    row.action === 'Open' && row.threshold_mode === 'LessOrEqual'
      ? 'GreaterOrEqual'
      : row.threshold_mode;
  return {
    targetNotional: String(row.target_notional),
    perOrderNotional: String(row.per_order_notional),
    thresholdMode,
    threshold: row.threshold != null ? String(row.threshold) : '0',
    minIntervalMs: String(row.min_order_interval_ms),
  };
}

function InstructionEditDialog({
  row,
  onClose,
  onSaved,
}: {
  row: InstructionView;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<InstructionEditDraft>(() => draftFromInstruction(row));
  const [saveError, setSaveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const hideTargetNotional = isPersistentCloseInstruction(row);

  useEffect(() => {
    setDraft(draftFromInstruction(row));
    setSaveError(null);
  }, [row]);

  const onSave = async () => {
    if (!isManualTradingConfigured()) {
      setSaveError('未配置 trading-engine');
      return;
    }
    const isPersistentClose = isPersistentCloseInstruction(row);
    const perOrder = Number(draft.perOrderNotional.trim());
    const minIv = Number(draft.minIntervalMs.trim());
    let target: number | undefined;
    if (!isPersistentClose) {
      target = Number(draft.targetNotional.trim());
      if (!(target > 0) || !Number.isFinite(target)) {
        setSaveError('目标名义须为有效正数');
        return;
      }
    }
    if (!(perOrder > 0) || !Number.isFinite(perOrder)) {
      setSaveError('单次名义须为有效正数');
      return;
    }
    if (!Number.isInteger(minIv) || minIv < 0) {
      setSaveError('下单间隔须为非负整数');
      return;
    }
    let threshold: number | null = null;
    if (draft.thresholdMode !== 'Immediate') {
      threshold = Number(draft.threshold.trim());
      if (!Number.isFinite(threshold)) {
        setSaveError('价差阈值须为有效数字');
        return;
      }
    }
    setSaveError(null);
    setBusy(true);
    try {
      await updateInstruction(row.instruction_id, {
        ...(target != null ? { target_notional: target } : {}),
        per_order_notional: perOrder,
        threshold_mode: draft.thresholdMode,
        threshold,
        min_order_interval_ms: minIv,
      });
      notifyInstructionsChanged();
      onSaved();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="instruction-threshold-dialog-backdrop" role="presentation" onClick={onClose}>
      <div
        className="instruction-threshold-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="修改指令"
        onClick={(event) => event.stopPropagation()}
      >
        <p className="instruction-threshold-dialog-title">
          修改指令 · {row.symbol} · {row.action === 'Close' ? '关仓' : '开仓'}
          {hideTargetNotional ? ' · 永久' : ''}
        </p>
        {hideTargetNotional ? (
          <p className="instruction-threshold-dialog-hint">永久关仓不设目标名义，每次按单次名义执行直至取消。</p>
        ) : null}
        {saveError ? <p className="instruction-threshold-dialog-error">{saveError}</p> : null}
        <div className="instruction-threshold-dialog-grid">
          {hideTargetNotional ? null : (
            <label>
              目标名义
              <input
                type="number"
                step="any"
                value={draft.targetNotional}
                disabled={busy}
                onChange={(event) => setDraft((prev) => ({ ...prev, targetNotional: event.target.value }))}
              />
            </label>
          )}
          <label>
            单次名义
            <input
              type="number"
              step="any"
              value={draft.perOrderNotional}
              disabled={busy}
              onChange={(event) => setDraft((prev) => ({ ...prev, perOrderNotional: event.target.value }))}
            />
          </label>
          <label>
            触发模式
            <select
              value={draft.thresholdMode}
              disabled={busy}
              onChange={(event) =>
                setDraft((prev) => ({
                  ...prev,
                  thresholdMode: event.target.value as ThresholdMode,
                }))
              }
            >
              {row.action === 'Open' ? (
                <>
                  <option value="GreaterOrEqual">价差 ≥ 阈值</option>
                  <option value="Immediate">立即</option>
                </>
              ) : (
                <>
                  <option value="GreaterOrEqual">价差 ≥ 阈值</option>
                  <option value="LessOrEqual">价差 ≤ 阈值</option>
                  <option value="Immediate">立即</option>
                </>
              )}
            </select>
          </label>
          {draft.thresholdMode !== 'Immediate' ? (
            <label>
              价差阈值
              <input
                type="number"
                step="any"
                value={draft.threshold}
                disabled={busy}
                onChange={(event) => setDraft((prev) => ({ ...prev, threshold: event.target.value }))}
              />
            </label>
          ) : null}
          <label>
            下单间隔 ms
            <input
              type="number"
              min={0}
              step={1}
              value={draft.minIntervalMs}
              disabled={busy}
              onChange={(event) => setDraft((prev) => ({ ...prev, minIntervalMs: event.target.value }))}
            />
          </label>
        </div>
        <div className="instruction-threshold-dialog-actions">
          <button type="button" className="instruction-threshold-dialog-btn" disabled={busy} onClick={onClose}>
            取消
          </button>
          <button
            type="button"
            className="instruction-threshold-dialog-btn is-primary"
            disabled={busy}
            onClick={() => void onSave()}
          >
            {busy ? '保存中…' : '保存修改'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function InstructionThresholdAxisOverlay({
  chartRef,
  chartContainerRef,
  openSpreadSeriesRef,
  closeSpreadSeriesRef,
  lines,
  instructions,
  chartSpreadScale,
  enabled,
}: Props) {
  const [paneHeight, setPaneHeight] = useState(0);
  const [axisItems, setAxisItems] = useState<AxisItemLayout[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [editingInstruction, setEditingInstruction] = useState<InstructionView | null>(null);

  const instructionById = useMemo(() => {
    const map = new Map<string, InstructionView>();
    for (const row of instructions) {
      map.set(row.instruction_id, row);
    }
    return map;
  }, [instructions]);

  const resolveSeries = useCallback(
    (line: InstructionThresholdLine): ISeriesApi<'Line'> | null =>
      line.action === 'Open' ? openSpreadSeriesRef.current : closeSpreadSeriesRef.current,
    [closeSpreadSeriesRef, openSpreadSeriesRef],
  );

  const updateLayout = useCallback(() => {
    const chart = chartRef.current;
    const container = chartContainerRef.current;
    if (!chart || !container || !enabled) {
      setPaneHeight(0);
      setAxisItems([]);
      return;
    }

    const spreadPane = chart.panes()[0];
    const nextPaneHeight = spreadPane?.getHeight() ?? 0;
    setPaneHeight(nextPaneHeight);

    const nextItems: AxisItemLayout[] = [];

    for (const line of lines) {
      const series = resolveSeries(line);
      if (!series) continue;
      const chartPrice = instructionThresholdToChartPrice(line.threshold, chartSpreadScale);
      const top = series.priceToCoordinate(chartPrice);
      if (top == null || !Number.isFinite(top) || top < 0 || top > nextPaneHeight) continue;
      nextItems.push({ line, top });
    }

    setAxisItems(nextItems);
  }, [chartContainerRef, chartRef, chartSpreadScale, enabled, lines, resolveSeries]);

  useEffect(() => {
    updateLayout();
  }, [updateLayout]);

  useEffect(() => {
    const chart = chartRef.current;
    const container = chartContainerRef.current;
    if (!chart || !container || !enabled) return undefined;

    updateLayout();
    chart.timeScale().subscribeVisibleLogicalRangeChange(updateLayout);
    chart.timeScale().subscribeSizeChange(updateLayout);
    const resizeObserver = new ResizeObserver(updateLayout);
    resizeObserver.observe(container);

    return () => {
      chart.timeScale().unsubscribeVisibleLogicalRangeChange(updateLayout);
      chart.timeScale().unsubscribeSizeChange(updateLayout);
      resizeObserver.disconnect();
    };
  }, [chartContainerRef, chartRef, enabled, updateLayout]);

  const onToggleStatus = useCallback(async (line: InstructionThresholdLine) => {
    if (!isManualTradingConfigured()) {
      setActionError('未配置 trading-engine');
      return;
    }
    setBusyId(line.instructionId);
    setActionError(null);
    try {
      if (line.status === 'Paused') {
        await resumeInstruction(line.instructionId);
      } else {
        await pauseInstruction(line.instructionId);
      }
      notifyInstructionsChanged();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  }, []);

  if (!enabled || axisItems.length === 0) {
    return editingInstruction ? (
      <InstructionEditDialog
        row={editingInstruction}
        onClose={() => setEditingInstruction(null)}
        onSaved={() => setEditingInstruction(null)}
      />
    ) : null;
  }

  return (
    <>
      <div className="instruction-threshold-axis-overlay" style={{ height: paneHeight }}>
        {axisItems.map(({ line, top }) => {
          const chartPrice = instructionThresholdToChartPrice(line.threshold, chartSpreadScale);
          const compactLabel = formatInstructionThresholdAxisCompactLabel(line, chartPrice);
          const instructionRow = instructionById.get(line.instructionId);
          const tooltipText = instructionRow
            ? activeInstructionChipTitle(instructionRow)
            : formatInstructionThresholdLineLabel(line, chartPrice, { includeStatusMarker: false });
          const tooltipLines = tooltipText.split('\n');
          const isPaused = line.status === 'Paused';
          const isBusy = busyId === line.instructionId;
          return (
            <div
              key={line.instructionId}
              className={`instruction-threshold-axis-item is-${line.action.toLowerCase()}${isPaused ? ' is-paused' : ''}`}
              style={{ top }}
            >
              <button
                type="button"
                className={`instruction-threshold-axis-status-btn${isPaused ? ' is-paused' : ' is-running'}`}
                disabled={isBusy || !isManualTradingConfigured()}
                aria-label={isPaused ? '恢复指令' : '暂停指令'}
                onClick={() => void onToggleStatus(line)}
              >
                {isBusy ? '…' : instructionStatusChartMarker(line.status)}
              </button>
              <span className="instruction-threshold-axis-label">{compactLabel}</span>
              <button
                type="button"
                className="instruction-threshold-axis-settings-btn"
                disabled={!instructionById.has(line.instructionId)}
                aria-label="修改指令"
                onClick={() => {
                  if (instructionRow) setEditingInstruction(instructionRow);
                }}
              >
                ⚙
              </button>
              <div className="instruction-threshold-axis-tooltip" role="tooltip">
                {tooltipLines.map((text, index) => (
                  <span key={index} className="instruction-threshold-axis-tooltip-line">
                    {text}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
        {actionError ? (
          <div className="instruction-threshold-axis-error" title={actionError}>
            {actionError}
          </div>
        ) : null}
      </div>
      {editingInstruction ? (
        <InstructionEditDialog
          row={editingInstruction}
          onClose={() => setEditingInstruction(null)}
          onSaved={() => setEditingInstruction(null)}
        />
      ) : null}
    </>
  );
}
